import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  Grid,
  useTheme,
  Typography,
  ButtonBase,
  useMediaQuery,
} from "@mui/material";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { LinearProgressBar, TabSwitch } from "../../customComponents";
import ContentWrapper from "../../Layout/ContentWrapper";
import GroupPickCard from "./GroupPickCard";
import ThirdPlaceList from "./ThirdPlaceList";

const GroupPicks = (props) => {
  const {
    focus,
    groups,
    navGroups,
    thirdPlaceTeams,
    updateGroupPicks,
    updateThirdPlacePicks,
    commitGroup,
    commitThirdPlace,
    setCardActive,
    groupProgress,
    stickyTopOffset = 0,
    tabs,
    currTab,
    handleTabChange,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isWide = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();

  // Measure the sticky header (title + group nav + progress) so the 3rd Place
  // Race column can pin just beneath it while the groups scroll. Only sticks at
  // lg+, where the race sits in its own side column (it stacks below otherwise).
  const headerContentRef = useRef(null);
  const [headerContentHeight, setHeaderContentHeight] = useState(0);
  useEffect(() => {
    const el = headerContentRef.current;
    if (!el) return;
    const update = () => setHeaderContentHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const thirdPlaceStickyTop = stickyTopOffset + headerContentHeight + 16;
  const currLocation = useLocation();

  const [currGroup, setCurrGroup] = useState();

  const setCurrentGroup = useCallback((newGroup) => {
    setCurrGroup(newGroup);
  }, []);

  useEffect(() => {
    if (currLocation?.pathname.includes("knockout")) {
      navigate("/picks/group");
    }
  }, [currLocation, navigate]);

  return (
    <Grid container sx={{ width: "100%", justifyContent: "center" }}>
      <ContentWrapper>
        <Grid container direction="column" sx={{ gap: "24px" }}>
          {/* header content — title, group nav, and progress; sticky beneath
              the page header (stepper + tab toggle) while the cards scroll */}
          <Grid
            container
            ref={headerContentRef}
            direction="column"
            sx={[
              {
                position: "sticky",
                top: `${stickyTopOffset}px`,
                zIndex: 55,
                backgroundColor: theme.branding.gray[0],
                gap: "14px",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                maxWidth: "calc(100vw - 48px)",
                paddingBottom: "6px",
              },
              (theme) =>
                theme.applyStyles("dark", {
                  backgroundColor: theme.branding.darkBlue[800],
                }),
            ]}
          >
            {/* title row — heading + subtitle on the left, stage toggle on the
                right (flattened to give the cards more room) */}
            <Grid
              container
              direction={isMobile ? "column" : "row"}
              wrap="nowrap"
              sx={{
                width: "100%",
                gap: "12px",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
              }}
            >
              <Grid
                container
                direction="column"
                wrap="nowrap"
                sx={{ width: "auto", gap: "2px", alignItems: "flex-start" }}
              >
                <Typography variant="h3" sx={{ letterSpacing: "0.4px" }}>
                  Group Stage Picks
                </Typography>
                <Grid
                  container
                  direction={isMobile ? "column" : "row"}
                  wrap="nowrap"
                  sx={{
                    gap: isMobile ? "2px" : "14px",
                    justifyContent: "flex-start",
                    alignItems: isMobile ? "flex-start" : "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={[
                      (theme) =>
                        theme.applyStyles("dark", {
                          color: theme.branding.gray[400],
                        }),
                    ]}
                  >
                    Drag group members to reorder • Choose which third-place
                    teams advance
                  </Typography>
                  <Link
                    to="/rules"
                    style={{
                      textDecoration: "none",
                      alignSelf: isMobile ? "flex-start" : "center",
                    }}
                  >
                    <Grid
                      container
                      direction="row"
                      wrap="nowrap"
                      sx={{
                        gap: "4px",
                        color: theme.branding.teal,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <BookOutlinedIcon
                        sx={{ height: "14px", width: "14px", color: "inherit" }}
                      />
                      <Typography
                        variant={isMobile ? "subtitle2" : "caption"}
                        sx={{ color: "inherit", fontWeight: 500 }}
                      >
                        Rules
                      </Typography>
                    </Grid>
                  </Link>
                </Grid>
              </Grid>

              {/* stage toggle (moved up from the page header) */}
              <Grid container sx={{ width: "auto", flexShrink: 0 }}>
                <TabSwitch
                  tabs={tabs}
                  currTab={currTab}
                  handleTabChange={handleTabChange}
                  context="picks"
                />
              </Grid>
            </Grid>

            {/* groups navigation bar */}
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={[
                {
                  position: "relative",
                  width: "100%",
                  minWidth: "50px",
                  padding: "10px 20px",
                  backgroundColor: theme.branding.gray[0],
                  borderBottom: `1px solid ${theme.branding.gray[200]}`,
                  boxShadow: `0 8px 20px -12px ${theme.branding.gray[200]}, 0 3px 5px -4px ${theme.branding.gray[300]}`,
                  overflow: "hidden",
                  scrollbarWidth: "none",
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    backgroundColor: theme.branding.darkBlue[800],
                    borderBottom: `1px solid ${theme.branding.darkBlue[600]}`,
                    boxShadow: `0 8px 20px -12px ${theme.branding.darkBlue[600]}, 0 3px 5px -4px ${theme.branding.darkBlue[800]}`,
                  }),
              ]}
            >
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={{
                  width: "100%",
                  overflow: "scroll",
                  whiteSpace: "nowrap",
                  scrollbarWidth: "none",
                  gap: "12px",
                }}
              >
                {navGroups?.map((g, index) => (
                  <ButtonBase
                    key={`group-nav-${index}`}
                    onClick={() => {}}
                    sx={[
                      {
                        padding: "2px",
                        borderRadius: "14px",
                        boxShadow: g?.complete
                          ? `0 13px 10px -13px ${theme.branding.cyan[300]}, 0 4px 4px -4px ${theme.branding.gray[100]}`
                          : "none",
                        //mock "border" around button for selected items
                        background: g?.complete
                          ? `linear-gradient(${theme.branding.cyan[500]}, ${theme.branding.cyan[300]})`
                          : theme.branding.gray[300],
                      },
                      (theme) =>
                        theme.applyStyles("dark", {
                          background: theme.branding.darkBlue[300],
                          boxShadow: `0 5px 13px -3px ${theme.branding.cyan[600]}, 0 4px 6px -4px ${theme.branding.cyan[800]}`,
                        }),
                    ]}
                  >
                    <Grid
                      container
                      direction="row"
                      wrap="nowrap"
                      sx={[
                        {
                          padding: "10px 20px",
                          gap: "4px",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "12px",
                          background: g?.complete
                            ? `radial-gradient(farthest-corner at 2px 2px, ${theme.branding.cyan[500]} 30%, ${theme.branding.cyan[600]} 100%)`
                            : theme.branding.gray[0],
                          color: g.complete
                            ? theme.branding.gray[0]
                            : "inherit",
                        },
                        (theme) =>
                          theme.applyStyles("dark", {
                            color: !g?.complete
                              ? theme.branding.gray[500]
                              : "inherit",
                          }),
                      ]}
                    >
                      <Typography variant="subtitle2" sx={{ color: "inherit" }}>
                        {g?.group}
                      </Typography>
                      {g?.complete && (
                        <CheckOutlinedIcon
                          sx={{
                            height: "14px",
                            width: "14px",
                            color: "inherit",
                          }}
                        />
                      )}
                    </Grid>
                  </ButtonBase>
                ))}
              </Grid>
            </Grid>

            {/* group picks progress tracking */}
            <Grid
              container
              direction="column"
              sx={{ width: "100%", gap: "16px" }}
            >
              <Grid
                container
                direction="row"
                sx={[
                  {
                    padding: "4px 12px",
                    gap: "4px",
                    alignItems: "center",
                    borderRadius: "16px",
                    backgroundColor: theme.branding.gray[100],
                    width: "fit-content",
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      backgroundColor: theme.branding.darkBlue[600],
                    }),
                ]}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: theme.branding.softTeal, fontSize: "16px" }}
                >
                  {groupProgress} / 12{" "}
                </Typography>
                <Typography variant="caption">Groups</Typography>
              </Grid>
              {/* group progress advances in 12 segments */}
              <Grid container sx={{ width: "100%" }}>
                <LinearProgressBar value={groupProgress * (100 / 12)} />
              </Grid>
            </Grid>
          </Grid>

          {/* picks grid */}
          <Grid
            container
            direction={isMobile ? "column" : "row"}
            spacing={4}
            columns={{ xs: 12 }}
          >
            {/* individual groups grid */}
            <Grid container spacing={3} size={{ xs: 12, lg: 8 }}>
              {groups.map((group, index) => (
                <Grid
                  key={`groups-${group?.groupId}-${index}`}
                  container
                  size={{ xs: 12, sm: 6, md: 4, lg: 6 }}
                >
                  <GroupPickCard
                    group={group}
                    updateGroupPicks={updateGroupPicks}
                    commitGroup={commitGroup}
                    setCardActive={setCardActive}
                    currGroup={currGroup}
                    setCurrentGroup={setCurrentGroup}
                  />
                </Grid>
              ))}
            </Grid>

            {/* third places selection — frozen on wide layouts so only the
                groups scroll; capped to the viewport so lower seeds stay
                reachable, and falls back to normal flow when stacked */}
            <Grid
              container
              size={{ xs: 12, lg: 4 }}
              sx={
                isWide
                  ? {
                      height: "fit-content",
                      alignSelf: "flex-start",
                      position: "sticky",
                      top: `${thirdPlaceStickyTop}px`,
                      maxHeight: `calc(100vh - ${thirdPlaceStickyTop}px - 16px)`,
                      overflowY: "auto",
                      scrollbarWidth: "none",
                    }
                  : { height: "fit-content" }
              }
            >
              <ThirdPlaceList
                thirdPlaceTeams={thirdPlaceTeams}
                updateThirdPlacePicks={updateThirdPlacePicks}
                commitThirdPlace={commitThirdPlace}
              />
            </Grid>
          </Grid>
        </Grid>
      </ContentWrapper>
    </Grid>
  );
};

export default GroupPicks;
